/**
 * The guide for brands: how a campaign runs from the request to the settlement, what the brand
 * pays, what the brief controls, and what Morcast checks before anything counts. The statements
 * follow the V1 specification (docs/specification.md).
 */

import { ArrowRight, ClipboardList, ScanSearch } from "lucide-react";
import { Link } from "react-router";

import { Band, bandButtons, CallToActionBand, SectionIntro } from "../../components/marketing.js";
import { Section, styles } from "../../components/ui.js";
import { SettlementExample } from "../home/visuals.js";
import { GuideHero, GuidePoints, GuideSteps } from "./parts.js";

const steps = [
  {
    title: "Send a request",
    text: "Describe the campaign with its platform, budget, target, dates and brief. Sending it is free.",
  },
  {
    title: "Morcast reviews it",
    text: "The team checks the terms and the brief, then accepts the request or rejects it with a note. A rejected request can be changed and sent again.",
  },
  {
    title: "Deposit the budget",
    text: "Deposit it from your wallet into the Morcast escrow on Base, in USDC or MOR, before the campaign starts. The escrow holds it, never Morcast.",
  },
  {
    title: "Creators publish",
    text: "Any creator with a linked X account or YouTube channel can take part during the window. Each submission is checked as it arrives.",
  },
  {
    title: "Results are measured and published",
    text: "After the end, Morcast measures each item's organic reach and checks it against the brief. The result is published with its evidence, and you can raise an issue during its review.",
  },
  {
    title: "The campaign settles",
    text: "Between five and ten days after the end, the escrow settles the campaign for the reach recognized. Creators claim their payouts, and you withdraw the rest of the budget. If it is not settled by Day 10, you can withdraw the whole budget.",
  },
];

export function ForBrands() {
  return (
    <>
      <GuideHero
        eyebrow="For brands"
        title="Pay for the reach creators actually deliver"
        documentTitle="For brands"
        actions={
          <>
            <Link to="/campaigns/new" className={styles.primaryButtonLarge}>
              Start a campaign
              <ArrowRight aria-hidden className="size-4" />
            </Link>
            <Link to="/campaigns" className={styles.secondaryButtonLarge}>
              See current campaigns
            </Link>
          </>
        }
        visual={<SettlementExample />}
      >
        Set a budget and a target, and Morcast pays creators only for verified organic reach. The
        budget waits in an on-chain escrow, and whatever is not earned comes back to you.
      </GuideHero>

      <Band
        tone="raised"
        innerClassName="grid items-start gap-6 py-16 sm:py-20 lg:grid-cols-[minmax(0,1fr)_26rem]"
      >
        <Section title="How a campaign runs" className="sm:p-8">
          <GuideSteps steps={steps} />
        </Section>
        <Section title="What you pay" className="lg:sticky lg:top-24 sm:p-8">
          <GuidePoints
            points={[
              "The budget in proportion to the reach recognized, up to the whole budget at the target.",
              "80% of it goes to creators, in proportion to their results, and 20% to Morcast.",
              "Nothing is paid for reach below a creator's threshold, or for content that fails the brief.",
            ]}
          />
        </Section>
      </Band>

      <Band innerClassName="py-16 sm:py-24">
        <SectionIntro
          eyebrow="Your brief, enforced"
          title="You set the rules and Morcast checks them"
        >
          Every item a creator submits is checked against your brief before its reach counts.
        </SectionIntro>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Section title="What the brief controls" icon={ClipboardList} className="sm:p-8">
            <GuidePoints
              points={[
                "The hashtags, mentions, links and phrases every item must include, and the disclosure it must show.",
                "Content and visual requirements, each checked on its own, and content that is not allowed.",
                "Which edits are allowed after submission, and how many items each of a creator's accounts may submit, up to 3.",
                "The target. Each creator's threshold is 1% of it, rounded up.",
              ]}
            />
          </Section>
          <Section title="What Morcast checks" icon={ScanSearch} className="sm:p-8">
            <GuidePoints
              points={[
                "That each item is public, published in the window and posted by the creator's linked account.",
                "That it includes every required element and was not edited beyond what the brief allows.",
                "Its content against each requirement, with the same pinned models every time. A person decides what the models cannot.",
                "Its reach against the account's own history. Results far above it go to a person before they count.",
              ]}
            />
          </Section>
        </div>
      </Band>

      <CallToActionBand
        title="Launch a campaign that pays only for results"
        actions={
          <>
            <Link to="/campaigns/new" className={bandButtons.primary}>
              Start a campaign
              <ArrowRight aria-hidden className="size-4" />
            </Link>
            <Link to="/campaigns" className={bandButtons.secondary}>
              See current campaigns
            </Link>
          </>
        }
      >
        Sending a request is free. You deposit the budget only once Morcast accepts it.
      </CallToActionBand>
    </>
  );
}
